import React from "react";
import { useAuth } from "../../../../contexts/AuthContext";
import { useProfileBoostViewModel } from "../viewModel/profileBoost.viewmodel";
import { Check, Info, RefreshCw, Wallet } from "lucide-react";

const CandidateProfileBoost: React.FC = () => {
  const { user } = useAuth();

  const verificationServices = [
    {
      id: "id-verification",
      icon: "🆔",
      title: "Identity Verification",
      description: "Verify your identity with government-issued ID.",
      benefits: [
        "Build trust with recruiters instantly",
        "Stand out from unverified profiles",
        "Reduce hiring friction",
        "Priority in search results",
      ],
      badge: "ID Verified",
      badgeColor: "bg-blue-100 text-blue-800 border-blue-200",
    },
    {
      id: "background-check",
      icon: "🛡️",
      title: "Background Verification",
      description:
        "Comprehensive background check including employment, education, and criminal history",
      benefits: [
        "Fast-track hiring process",
        "Enhanced profile visibility",
        "Build recruiter confidence",
        "Access to exclusive opportunities",
      ],
      badge: "Not Verified",
      badgeColor: "bg-green-100 text-green-800 border-green-200",
      subServices: [
        "Employment History Verification",
        "Education Credentials Check",
        "Professional References",
        "Criminal Background Check",
        "Credit Check (optional)",
        "Social Media Screening",
      ],
    },
  ];
  const {
    state,
    dispatch,
    handleIdentityVerifyStatus,
    handleVerifyIdentity,
    handleStripePayment,
  } = useProfileBoostViewModel();
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-12">
          <div className="flex items-center justify-center mb-4">
            <div className="bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full p-2 sm:p-3">
              <span className="text-xl sm:text-2xl">✨</span>
            </div>
          </div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 sm:mb-4 px-2">
            Profile Boost
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 mb-2 px-2">
            Stand Out to Recruiters with Verified Credentials
          </p>
          <p className="text-sm sm:text-base text-gray-500 max-w-2xl mx-auto px-4">
            Elevate your profile with professional verification services that
            build trust, increase visibility, and unlock premium job
            opportunities.
          </p>
        </div>

        {/* Coming Soon Banner */}
        {/* <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg p-4 sm:p-6 mb-6 sm:mb-8 text-white text-center mx-2 sm:mx-0">
          <div className="flex items-center justify-center mb-3">
            <span className="text-2xl sm:text-3xl mr-2 sm:mr-3">🚀</span>
            <h2 className="text-xl sm:text-2xl font-bold">Coming Soon!</h2>
          </div>
          <p className="text-base sm:text-lg opacity-90 mb-3 sm:mb-4 px-2">
            We're working hard to bring you premium verification services
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-2 sm:space-y-0 sm:space-x-4 text-xs sm:text-sm opacity-80">
            <div className="flex items-center">
              <span className="w-2 h-2 bg-white rounded-full mr-2"></span>
              Expected Launch: Q4 2025
            </div>
            <div className="flex items-center">
              <span className="w-2 h-2 bg-white rounded-full mr-2"></span>
              Early Bird Discounts Available
            </div>
          </div>
        </div> */}

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 sm:gap-8 mb-8 sm:mb-12 px-2 sm:px-0">
          {verificationServices.map((service) => (
            <div
              key={service.id}
              className={`rounded-lg shadow-sm border overflow-hidden ${
                service.id === "background-check"
                  ? "bg-gray-50 border-gray-200 opacity-70"
                  : "bg-white border-gray-200"
              }`}
            >
              <div className="p-4 sm:p-6 border-b border-gray-100">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start sm:items-center flex-col sm:flex-row">
                    <div
                      className={`rounded-lg p-2 sm:p-3 mb-3 sm:mb-0 sm:mr-4 self-start ${
                        service.id === "background-check"
                          ? "bg-gray-200 grayscale"
                          : "bg-gray-100"
                      }`}
                    >
                      <span className="text-xl sm:text-2xl">
                        {service.icon}
                      </span>
                    </div>
                    <div className="flex-1">
                      <h3
                        className={`text-lg sm:text-xl font-semibold mb-1 ${
                          service.id === "background-check"
                            ? "text-gray-500"
                            : "text-gray-900"
                        }`}
                      >
                        {service.title}
                      </h3>
                      <p
                        className={`text-sm sm:text-base ${
                          service.id === "background-check"
                            ? "text-gray-400"
                            : "text-gray-600"
                        }`}
                      >
                        {service.description}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Badge Preview */}
                <div className="flex items-center">
                  <span
                    className={`inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium border ${
                      service.id === "id-verification"
                        ? state.identityVerifyStatusModel?.identity_status ===
                          "verified"
                          ? "bg-green-100 text-green-800 border-green-200"
                          : state.identityVerifyStatusModel?.identity_status ===
                            "processing"
                          ? "bg-blue-100 text-blue-800 border-blue-200"
                          : state.identityVerifyStatusModel?.identity_status ===
                            "requires_input"
                          ? "bg-red-100 text-red-800 border-red-200"
                          : service.badgeColor
                        : service.id === "background-check"
                        ? "bg-gray-100 text-gray-500 border-gray-200"
                        : service.badgeColor
                    }`}
                  >
                    {service.id === "id-verification"
                      ? state.identityVerifyStatusModel?.identity_status ??
                        service.badge
                      : service.badge}
                  </span>
                </div>
              </div>

              {/* Benefits */}
              <div className="p-4 sm:p-6">
                <h4
                  className={`font-medium mb-3 text-sm sm:text-base ${
                    service.id === "background-check"
                      ? "text-gray-500"
                      : "text-gray-900"
                  }`}
                >
                  Key Benefits:
                </h4>
                <ul className="space-y-2 mb-4 sm:mb-6">
                  {service.benefits.map((benefit, index) => (
                    <li key={index} className="flex items-start">
                      <span
                        className={`mr-2 mt-0.5 flex-shrink-0 ${
                          service.id === "background-check"
                            ? "text-gray-400"
                            : "text-emerald-500"
                        }`}
                      >
                        ✓
                      </span>
                      <span
                        className={`text-xs sm:text-sm ${
                          service.id === "background-check"
                            ? "text-gray-400"
                            : "text-gray-600"
                        }`}
                      >
                        {benefit}
                      </span>
                    </li>
                  ))}
                </ul>

                {/* Sub-services for Background Check */}
                {service.subServices && (
                  <div className="mb-4 sm:mb-6">
                    <h4 className="font-medium text-gray-500 mb-3 text-sm sm:text-base">
                      Verification Includes:
                    </h4>
                    <div className="grid grid-cols-1 gap-2">
                      {service.subServices.map((subService, index) => (
                        <div
                          key={index}
                          className="flex items-center text-xs sm:text-sm text-gray-400"
                        >
                          <span className="w-1.5 h-1.5 bg-gray-300 rounded-full mr-2 flex-shrink-0"></span>
                          {subService}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {service.id === "id-verification" && (
                  <div className="w-full max-w-4xl">
                    <div className="bg-white rounded-lg border border-slate-200/80 shadow-sm p-4 sm:p-4 flex items-center gap-5">
                      <div className="shrink-0 w-14 h-14 rounded-xl bg-indigo-50 flex items-center justify-center">
                        <Wallet
                          className="w-7 h-7 text-indigo-600"
                          strokeWidth={2.2}
                        />
                      </div>

                      <div className="flex-1 min-w-0">
                        <h2 className="text-lg sm:text-xl font-semibold text-slate-900">
                          {state.identityVerifyStatusModel?.disable_pay
                            ? "Payment Details"
                            : "Pay for Verification"}
                        </h2>
                        <p className="text-sm text-slate-500 mt-0.5">
                          One-time payment for identity verification
                        </p>
                      </div>

                      <div className="flex flex-col items-end gap-2 shrink-0">
                        <div className="text-xl sm:text-xl font-bold text-indigo-600 leading-none">
                          {state.identityVerifyStatusModel?.display_price}
                        </div>
                        <div className="text-xs text-slate-500">
                          One-time fee
                        </div>
                        {state.identityVerifyStatusModel?.payment_status ===
                          "paid" && (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-medium border border-emerald-100">
                            <Check className="w-3.5 h-3.5" strokeWidth={3} />
                            Paid
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="flex  gap-4 mt-4">
                  {service.id === "id-verification" && (
                    <button
                      disabled={state.identityVerifyStatusModel?.disable_pay}
                      onClick={handleStripePayment}
                      className={`w-full py-2 sm:py-3 px-4 rounded-lg flex flex-col  font-medium text-sm sm:text-base ${
                        state.identityVerifyStatusModel?.disable_pay
                          ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                          : "bg-blue-600 text-white hover:bg-blue-700 cursor-pointer"
                      }`}
                    >
                      <span className="font-semibold text-xl">💳 Pay Now</span>
                      <p className="text-xs mt-1 leading-tight">
                        {state.identityVerifyStatusModel?.disable_pay
                          ? "Payment Completed"
                          : "procede to payment to enable verification"}
                      </p>
                    </button>
                  )}

                  {/* Verify Button */}
                  {service.id === "background-check" ? (
                    <button
                      disabled
                      className="w-full py-2 sm:py-3 px-4 rounded-lg font-medium text-sm sm:text-base bg-gray-100 text-gray-400 cursor-not-allowed"
                    >
                      Not verified
                    </button>
                  ) : (
                    <button
                      disabled={state.identityVerifyStatusModel?.disable_verify}
                      onClick={handleVerifyIdentity}
                      className={` flex flex-col items-center w-full py-2 sm:py-3 px-4 rounded-lg font-medium text-sm sm:text-base ${
                        state.identityVerifyStatusModel?.disable_verify
                          ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                          : "bg-blue-600 text-white hover:bg-blue-700 cursor-pointer"
                      }`}
                    >
                      <span className="font-semibold text-xl">
                        🛡️ Verify Now
                      </span>
                      <span className="text-sm mt-1">
                        Start identity verification
                      </span>
                    </button>
                  )}
                </div>

                <div className="mt-4">
                  {service.id === "id-verification" && (
                    <div className="flex items-center justify-center gap-2 py-4">
                      <div className="flex items-center justify-cente">
                        <svg
                          className={`${
                            state.identityVerifyStatusModel?.disable_pay
                              ? " w-5 h-5 text-green-400 "
                              : " w-5 h-5 text-gray-600"
                          }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M12 11c0 .552-.448 1-1 1s-1-.448-1-1 .448-1 1-1 1 .448 1 1zm6-2V7a5 5 0 10-10 0v2H6v11h12V9h-2zm-8-2a3 3 0 116 0v2h-6V7z"
                          />
                        </svg>
                      </div>

                      <p
                        className={`${
                          state.identityVerifyStatusModel?.disable_pay
                            ? " text-sm text-green-400 "
                            : " text-sm text-gray-600"
                        }`}
                      >
                        Secure payment. Your information is safe with us.
                      </p>
                    </div>
                  )}
                </div>
                {service.id === "id-verification" &&
                  (state.identityVerifyStatusModel?.disable_pay ? (
                    <div className="mt-3 px-2 py-2.5 flex gap-1 text-sm text-blue-700">
                      <RefreshCw className="w-4 h-4" strokeWidth={2.5} />
                      <span>
                        <span className="font-normal">
                          Verification attempts included:
                        </span>
                        {state.identityVerifyStatusModel?.max_retry}
                      </span>
                      <span className="text-blue-300">|</span>
                      <span>
                        <span className="font-normal">Attempts remaining:</span>{" "}
                        {state.identityVerifyStatusModel?.retry_count}
                        of {state.identityVerifyStatusModel?.max_retry}
                      </span>
                      <Info className="w-4 h-4 text-sky-500" />
                    </div>
                  ) : null)}
              </div>
            </div>
          ))}
        </div>

        {/* Why Verification Matters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 lg:p-8 mx-2 sm:mx-0">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6 text-center px-2">
            Why Verification Matters in Today's Job Market
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            <div className="text-center px-2">
              <div className="bg-blue-100 rounded-full p-3 sm:p-4 w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 flex items-center justify-center">
                <span className="text-xl sm:text-2xl">🎯</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base">
                3x Higher Response Rate
              </h3>
              <p className="text-gray-600 text-xs sm:text-sm">
                Verified profiles receive significantly more recruiter responses
                and interview invitations
              </p>
            </div>
            <div className="text-center px-2">
              <div className="bg-green-100 rounded-full p-3 sm:p-4 w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 flex items-center justify-center">
                <span className="text-xl sm:text-2xl">⚡</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base">
                Faster Hiring Process
              </h3>
              <p className="text-gray-600 text-xs sm:text-sm">
                Skip lengthy background checks and move directly to final
                interview rounds
              </p>
            </div>
            <div className="text-center px-2">
              <div className="bg-purple-100 rounded-full p-3 sm:p-4 w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 flex items-center justify-center">
                <span className="text-xl sm:text-2xl">💼</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base">
                Professional Credibility
              </h3>
              <p className="text-gray-600 text-xs sm:text-sm">
                Build trust and showcase your commitment to transparency and
                professionalism
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CandidateProfileBoost;
